import React, { useEffect, useState } from "react";
import * as FaIcons from "react-icons/fa";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import Loader from "../Loader/Loader";
import GstSettingModal from "./GstSettingModal";
import {
  getCountryNameByCode,
  resolveCountryMetaByCode,
} from "../../utilis/countryCurrency";
import { invalidateInvoiceCatalog } from "../../utilis/invoiceWorkspaceApi";

const emptyForm = {
  id: null,
  country_name: "",
  country_code: "",
  currency_code: "",
  gst_percentage: "",
};

function GstSetting() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [getLoading, setGetLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
  });

  const getGstSettings = async () => {
    setGetLoading(true);
    try {
      const response = await apiUrl.get("gstSettings", {
        params: {
          page: pagination.currentPage,
          perPage: pagination.perPage,
        },
      });

      if (response?.data?.status) {
        setSettings(response.data.data || []);
        if (response.data.pagination) {
          setPagination({
            currentPage: response.data.pagination.currentPage,
            perPage: response.data.pagination.perPage,
            total: response.data.pagination.total,
            lastPage: response.data.pagination.lastPage,
          });
        }
      }
    } catch (error) {
      console.error("Failed to fetch GST settings:", error);
    } finally {
      setGetLoading(false);
    }
  };

  useEffect(() => {
    getGstSettings();
  }, [pagination.currentPage, pagination.perPage]);

  const resetForm = () => {
    setFormData(emptyForm);
    setIsEditing(false);
  };

  const handleAdd = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEdit = async (setting) => {
    try {
      setLoading(true);
      const response = await apiUrl.get(`gstSettings/${setting.id}`);
      const record = response?.data?.data || setting;
      const meta = resolveCountryMetaByCode(record.country_code);

      setFormData({
        id: record.id,
        country_name: meta.country_name,
        country_code: record.country_code || meta.country_code,
        currency_code: record.currency_code || meta.currency_code,
        gst_percentage: record.gst_percentage ?? "",
      });
      setIsEditing(true);
      setShowModal(true);
    } catch (error) {
      Swal.fire({
        title: "Failed to load GST setting",
        text: error.response?.data?.message || "An error occurred",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (setting) => {
    const countryLabel = getCountryNameByCode(setting.country_code);
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: `Delete GST setting for "${countryLabel}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      const response = await apiUrl.post(`gstSettings/${setting.id}/delete`);

      if (response?.data?.status) {
        invalidateInvoiceCatalog();
        Swal.fire({
          title: "GST setting deleted successfully!",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        getGstSettings();
      }
    } catch (error) {
      Swal.fire({
        title: "Failed to delete GST setting",
        text: error.response?.data?.message || "An error occurred",
        icon: "error",
      });
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        country_code: formData.country_code,
        currency_code: formData.currency_code,
        gst_percentage: Number(formData.gst_percentage),
      };

      const response = isEditing
        ? await apiUrl.post(`gstSettings/${formData.id}/update`, payload)
        : await apiUrl.post("gstSettings", payload);

      if (response?.data?.status) {
        invalidateInvoiceCatalog();
        Swal.fire({
          title: response?.data?.message || "GST setting saved successfully!",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        setShowModal(false);
        resetForm();
        getGstSettings();
      }
    } catch (error) {
      Swal.fire({
        title: "Failed to save GST setting",
        text: error.response?.data?.message || "An error occurred",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = ({ selected }) => {
    setPagination((prev) => ({
      ...prev,
      currentPage: selected + 1,
    }));
  };

  const handlePerPageChange = (e) => {
    setPagination((prev) => ({
      ...prev,
      perPage: parseInt(e.target.value, 10),
      currentPage: 1,
    }));
  };

  return (
    <div className="container-fluid py-4">
      <div className="container">
        <div
          className="card mb-4 shadow-sm border-0"
          style={{ background: "linear-gradient(90deg, #ffffff, #d2e8f8)" }}
        >
          <div className="card-body d-flex align-items-center">
            <div
              className="me-4 d-flex justify-content-center align-items-center rounded-circle"
              style={{
                width: "50px",
                height: "50px",
                backgroundColor: "#435690",
              }}
            >
              <FaIcons.FaPercentage className="text-white fs-4" />
            </div>
            <div>
              <h1 className="h3 mb-1 fw-bold text-dark">GST Settings</h1>
              <p className="text-muted mb-0">
                Manage GST configuration by country and currency.
              </p>
            </div>
          </div>
        </div>

        <div className="card shadow-sm">
          <div className="card-body">
            <div className="d-flex justify-content-end mb-4">
              <button
                type="button"
                className="btn btn-primary text-white d-inline-flex align-items-center"
                onClick={handleAdd}
              >
                <FaIcons.FaPlusCircle className="me-2" size={18} />
                Add GST Setting
              </button>
            </div>

            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Country</th>
                    <th>Country Code</th>
                    <th>Currency</th>
                    <th>GST %</th>
                    <th>Updated</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getLoading ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        <Loader />
                      </td>
                    </tr>
                  ) : settings.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-5 text-muted">
                        No GST settings found. Click &quot;Add GST Setting&quot; to
                        create one.
                      </td>
                    </tr>
                  ) : (
                    settings.map((setting) => (
                      <tr key={setting.id}>
                        <td className="fw-medium">
                          {getCountryNameByCode(setting.country_code)}
                        </td>
                        <td className="font-monospace">
                          {setting.country_code}
                        </td>
                        <td className="font-monospace">
                          {setting.currency_code}
                        </td>
                        <td>{setting.gst_percentage}%</td>
                        <td className="text-muted">
                          {setting.updated_at
                            ? new Date(setting.updated_at).toLocaleDateString()
                            : setting.created_at
                              ? new Date(setting.created_at).toLocaleDateString()
                              : "—"}
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm me-2"
                            onClick={() => handleEdit(setting)}
                            title="Edit"
                          >
                            <FaIcons.FaPencilAlt />
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDelete(setting)}
                            title="Delete"
                          >
                            <FaIcons.FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="row align-items-center mt-3 mb-2">
              <div className="col-auto">
                <select
                  value={pagination.perPage}
                  onChange={handlePerPageChange}
                  className="form-select"
                  style={{ minWidth: 80 }}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
              <div className="col-auto text-muted">
                Page {pagination.currentPage} of {pagination.lastPage || 1} —{" "}
                {pagination.total} entries
              </div>
              <div className="col">
                <ReactPaginate
                  previousLabel="Previous"
                  nextLabel="Next"
                  breakLabel="..."
                  pageCount={pagination.lastPage || 1}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={3}
                  onPageChange={handlePageChange}
                  forcePage={Math.max(pagination.currentPage - 1, 0)}
                  containerClassName="pagination justify-content-end mb-0"
                  pageClassName="page-item"
                  pageLinkClassName="page-link"
                  previousClassName="page-item"
                  previousLinkClassName="page-link"
                  nextClassName="page-item"
                  nextLinkClassName="page-link"
                  activeClassName="active"
                  disabledClassName="disabled"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <GstSettingModal
        show={showModal}
        onClose={() => {
          resetForm();
          setShowModal(false);
        }}
        onSave={handleSave}
        loading={loading}
        formData={formData}
        setFormData={setFormData}
        isEditing={isEditing}
      />
    </div>
  );
}

export default GstSetting;
